import * as React from "react"

import type { ToastProps } from "./Toast"
import { getUserFacingErrorMessage } from "./userFacingErrors"

const TOAST_LIMIT = 5
const TOAST_DISMISS_DELAY = 4000

type ToastVariant = NonNullable<ToastProps["variant"]>

interface ToastActionInput {
  label: string
  onClick: () => void
}

interface ToastEntry {
  id: string
  variant: ToastVariant
  title?: string
  description?: string
  icon?: React.ReactNode
  action?: ToastActionInput
  duration?: number
  open: boolean
}

type ToastInput = Omit<ToastEntry, "id" | "open" | "variant">

type ErrorToastInput = Omit<ToastInput, "description"> & {
  description?: string
  error: unknown
  fallback?: string
}

type ToastStateAction =
  | { type: "ADD"; toast: ToastEntry }
  | { type: "DISMISS"; id: string }
  | { type: "REMOVE"; id: string }

interface ToastApi {
  (input: ToastInput & { variant?: ToastVariant }): string
  info: (input: ToastInput | string) => string
  success: (input: ToastInput | string) => string
  warning: (input: ToastInput | string) => string
  danger: (input: ToastInput | string) => string
  error: (input: ErrorToastInput) => string
  dismiss: (id: string) => void
}

let count = 0
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

const listeners: Array<(state: ToastEntry[]) => void> = []
let memoryState: ToastEntry[] = []

function dispatch(action: ToastStateAction) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => listener(memoryState))
}

function reducer(state: ToastEntry[], action: ToastStateAction): ToastEntry[] {
  switch (action.type) {
    case "ADD":
      return [action.toast, ...state].slice(0, TOAST_LIMIT)
    case "DISMISS":
      return state.map((t) => (t.id === action.id ? { ...t, open: false } : t))
    case "REMOVE":
      return state.filter((t) => t.id !== action.id)
  }
}

function createToast(variant: ToastVariant, input: ToastInput | string) {
  const id = genId()
  const entry: ToastEntry =
    typeof input === "string"
      ? { id, variant, title: input, open: true }
      : { id, variant, ...input, open: true }

  dispatch({ type: "ADD", toast: entry })
  return id
}

const toast = ((input: ToastInput & { variant?: ToastVariant }) => {
  return createToast(input.variant ?? "info", input)
}) as ToastApi

toast.info = (input: ToastInput | string) => createToast("info", input)
toast.success = (input: ToastInput | string) => createToast("success", input)
toast.warning = (input: ToastInput | string) => createToast("warning", input)
toast.danger = (input: ToastInput | string) => createToast("danger", input)
toast.error = ({ error, fallback, ...input }: ErrorToastInput) =>
  createToast("danger", {
    ...input,
    description:
      input.description ?? getUserFacingErrorMessage(error, { fallback }),
  })
toast.dismiss = (id: string) => dispatch({ type: "DISMISS", id })

function useToast() {
  const [toasts, setToasts] = React.useState(memoryState)

  React.useEffect(() => {
    listeners.push(setToasts)
    return () => {
      const index = listeners.indexOf(setToasts)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [])

  return {
    toasts,
    toast,
    dismiss: (id: string) => dispatch({ type: "DISMISS", id }),
    remove: (id: string) => dispatch({ type: "REMOVE", id }),
  }
}

export { useToast, toast, TOAST_DISMISS_DELAY }
export type { ErrorToastInput, ToastActionInput, ToastEntry, ToastInput }
